import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, Typography, Tag, Button, Row, Col, StatCard, Space, Tooltip, Select, DatePicker } from '../../ui';
import type { TagTone } from '../../ui';
import { Plus, Eye, Pencil, Calendar, CalendarDays, Users, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';
import { EventFormModal } from './EventFormModal';

const STATUS_COLOR: Record<string, TagTone> = { PLANNED: 'blue', ONGOING: 'gold', COMPLETED: 'green', CANCELLED: 'red' };
const TYPE_LABEL: Record<string, string> = {
  RETAILER_MEET: 'Retailer meet', FARMER_MEET: 'Farmer meet', TRAINING: 'Training', SEMINAR: 'Seminar',
  PRODUCT_LAUNCH: 'Product launch', FIELD_DAY: 'Field day', MEETING: 'Meeting', OTHER: 'Other',
};
const statusLabel = (s: string) => (s ?? '').replace(/_/g, ' ');

export function EventsListPage() {
  const navigate = useNavigate();
  const canCreate = usePermission('EVENT.CREATE');
  const canEdit = usePermission('EVENT.EDIT');
  const [status, setStatus] = useState<string | undefined>();
  const [type, setType] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [formOpen, setFormOpen] = useState(false);
  const [editRec, setEditRec] = useState<any | null>(null);
  const from = range[0]?.format('YYYY-MM-DD');
  const to = range[1]?.format('YYYY-MM-DD');

  const { data, isLoading } = useQuery({
    queryKey: ['events', status, type, from, to],
    queryFn: () => request<any[]>({ url: '/events', params: { status, type, from, to } }),
    placeholderData: keepPreviousData,
  });
  const rows = data ?? [];

  const kpis = useMemo(() => {
    const now = dayjs();
    const monthStart = now.startOf('month'), monthEnd = now.endOf('month');
    let upcoming = 0, thisMonth = 0, completed = 0, attendees = 0;
    for (const e of rows) {
      const start = dayjs(e.startAt);
      if (e.status === 'PLANNED' && start.isAfter(now)) upcoming++;
      if (!start.isBefore(monthStart) && !start.isAfter(monthEnd)) thisMonth++; // inclusive: an event at exactly 00:00 on the 1st counts
      if (e.status === 'COMPLETED') { completed++; attendees += Number(e.attendedCount ?? 0); }
    }
    return { upcoming, thisMonth, completed, attendees, total: rows.length };
  }, [rows]);

  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Event No', dataIndex: 'code', sortable: true, width: 150, alwaysVisible: true },
    {
      key: 'title', title: 'Title', alwaysVisible: true,
      render: (_: any, e: any) => (
        <div>
          <div style={{ fontWeight: 600 }}>{e.title}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {TYPE_LABEL[e.type] ?? e.type}{e.cadence && e.cadence !== 'ONE_TIME' ? ` · ${e.cadence.toLowerCase()}` : ''}
          </div>
        </div>
      ),
    },
    { key: 'startAt', title: 'When', dataIndex: 'startAt', sortable: true, width: 180, render: (v: any) => formatIst(v) },
    { key: 'territory', title: 'Territory', width: 160, render: (_: any, e: any) => e.territoryName ?? <span style={{ color: '#bbb' }}>—</span> },
    { key: 'organiser', title: 'Organiser', width: 150, render: (_: any, e: any) => e.organiserName ?? '—' },
    {
      key: 'people', title: 'Invited / Attended', width: 150, align: 'right',
      render: (_: any, e: any) => (
        <span><b>{e.invitedCount ?? 0}</b>{e.status === 'COMPLETED' ? <span style={{ color: '#52C41A' }}> · {e.attendedCount ?? 0} ✓</span> : null}</span>
      ),
    },
    { key: 'photoCount', title: 'Photos', dataIndex: 'photoCount', width: 80, align: 'right', defaultVisible: false },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 120, render: (v: string) => <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{statusLabel(v)}</Tag> },
    {
      key: 'actions', title: 'Actions', width: 120, align: 'right', alwaysVisible: true,
      render: (_: any, e: any) => (
        <div onClick={(ev) => ev.stopPropagation()}>
          <Space size={4}>
            {canEdit && (
              <Tooltip label="Edit"><Button variant="text" icon={<Pencil size={16} />} aria-label="Edit" onClick={() => { setEditRec(e); setFormOpen(true); }} /></Tooltip>
            )}
            <Tooltip label="Open"><Button variant="text" icon={<Eye size={16} />} aria-label="Open" onClick={() => navigate(`/events/${e.id}`)} /></Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Events &amp; Meets</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditRec(null); setFormOpen(true); }}>New Event</Button>
          ) : undefined}
        </div>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><StatCard label="Upcoming" value={kpis.upcoming} icon={<Calendar />} tone="brand" /></Col>
        <Col xs={12} md={6}><StatCard label="This month" value={kpis.thisMonth} icon={<CalendarDays />} tone="sky" /></Col>
        <Col xs={12} md={6}><StatCard label="Completed" value={kpis.completed} icon={<CheckCircle2 />} tone="success" /></Col>
        <Col xs={12} md={6}><StatCard label="Attendees (completed)" value={kpis.attendees} icon={<Users />} tone="gold" /></Col>
      </Row>

      <Card>
        <DataTable
          storageKey="events-list"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          onRow={(e: any) => ({ onClick: () => navigate(`/events/${e.id}`), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <>
              <Select placeholder="Status" allowClear style={{ width: 150 }} value={status} onChange={setStatus}
                options={Object.keys(STATUS_COLOR).map((s) => ({ value: s, label: statusLabel(s) }))} />
              <Select placeholder="Type" allowClear showSearch optionFilterProp="label" style={{ width: 170 }} value={type} onChange={setType}
                options={Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
              <DatePicker.RangePicker value={range as any} onChange={(r) => setRange((r as any) ?? [null, null])} allowEmpty={[true, true]} />
            </>
          }
        />
      </Card>

      <EventFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditRec(null); }} record={editRec} />
    </div>
  );
}
